/**
 * KHIYARSA (Phase 2): cloud UI — auth dialog, My Projects, version history,
 * conflict resolution, and the sync status chip. Rendered as children of
 * <Excalidraw> (needs the editor context for Dialog components).
 */
import ConfirmDialog from "@excalidraw/excalidraw/components/ConfirmDialog";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { TextField } from "@excalidraw/excalidraw/components/TextField";
import {
  LoadIcon,
  TrashIcon,
  historyIcon,
} from "@excalidraw/excalidraw/components/icons";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAtom, useAtomValue, useSetAtom } from "../../app-jotai";
import { useAppLangCode } from "../../app-language/language-state";

import { ApiError, cloudApi } from "./api";
import {
  attachedProjectAtom,
  cloudConflictAtom,
  cloudDialogAtom,
  cloudUserAtom,
  conflictDialogDismissedAtom,
  syncStatusAtom,
} from "./atoms";
import { cloudSync } from "./sync";
import { useCloudT } from "./strings";

import type { ProjectMeta } from "./api";

import "./cloud.scss";

const errorKey = (error: unknown) => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "loginFailed" as const;
    }
    if (error.status === 409) {
      return "emailExists" as const;
    }
    if (error.status === 429) {
      return "tooManyAttempts" as const;
    }
    if (error.code === "invalid_password") {
      return "passwordTooShort" as const;
    }
  }
  return "genericError" as const;
};

// ---------------------------------------------------------------------------
// Auth dialog
// ---------------------------------------------------------------------------

const AuthDialog = ({ onClose }: { onClose: () => void }) => {
  const t = useCloudT();
  const setDialog = useSetAtom(cloudDialogAtom);
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (busy || !email || !password) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res =
        mode === "signin"
          ? await cloudApi.login(email, password)
          : await cloudApi.register(email, password, displayName || undefined);
      cloudSync.setUser(res.user);
      setDialog({ type: "projects" });
    } catch (err) {
      setError(t(errorKey(err)));
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={mode === "signin" ? t("signIn") : t("createAccount")}
      size="small"
      onCloseRequest={onClose}
    >
      <div className="khiyarsa-cloud-auth">
        <TextField
          label={t("email")}
          value={email}
          placeholder="you@example.com"
          fullWidth
          onChange={setEmail}
          onKeyDown={(event) => event.key === "Enter" && submit()}
        />
        <TextField
          label={t("password")}
          value={password}
          placeholder="••••••••"
          fullWidth
          isRedacted
          onChange={setPassword}
          onKeyDown={(event) => event.key === "Enter" && submit()}
        />
        {mode === "signup" && (
          <TextField
            label={t("displayName")}
            value={displayName}
            fullWidth
            onChange={setDisplayName}
            onKeyDown={(event) => event.key === "Enter" && submit()}
          />
        )}
        {error && <div className="khiyarsa-cloud-error">{error}</div>}
        <button
          className="khiyarsa-cloud-btn khiyarsa-cloud-btn--primary"
          disabled={busy || !email || password.length < (mode === "signup" ? 8 : 1)}
          onClick={submit}
        >
          {mode === "signin" ? t("signIn") : t("createAccount")}
        </button>
        <button
          className="khiyarsa-cloud-btn khiyarsa-cloud-btn--link"
          onClick={() => {
            setMode(mode === "signin" ? "signup" : "signin");
            setError(null);
          }}
        >
          {mode === "signin" ? t("createAccount") : t("signIn")}
        </button>
      </div>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Projects dialog
// ---------------------------------------------------------------------------

const relativeTime = (iso: string, langCode: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const rtf = new Intl.RelativeTimeFormat(
    langCode?.startsWith("ar") ? "ar" : "en",
    { numeric: "auto" },
  );
  const minutes = Math.round(diff / 60000);
  if (minutes < 60) {
    return rtf.format(-minutes, "minute");
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return rtf.format(-hours, "hour");
  }
  const days = Math.round(hours / 24);
  if (days < 30) {
    return rtf.format(-days, "day");
  }
  return new Date(iso).toLocaleDateString(
    langCode?.startsWith("ar") ? "ar" : "en",
  );
};

type PendingConfirm = {
  title: string;
  message: string;
  action: () => Promise<void>;
} | null;

const ProjectsDialog = ({ onClose }: { onClose: () => void }) => {
  const t = useCloudT();
  const [langCode] = useAppLangCode();
  const user = useAtomValue(cloudUserAtom);
  const attach = useAtomValue(attachedProjectAtom);
  const setDialog = useSetAtom(cloudDialogAtom);

  const [projects, setProjects] = useState<ProjectMeta[] | null>(null);
  const [showTrash, setShowTrash] = useState(false);
  const [search, setSearch] = useState("");
  const [usage, setUsage] = useState<{
    projects: number;
    projectsLimit: number;
    fileBytes: number;
    sceneBytes: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [confirm, setConfirm] = useState<PendingConfirm>(null);
  const searchSeq = useRef(0);

  const reload = useCallback(async (trash: boolean, query = "") => {
    const seq = ++searchSeq.current;
    setError(null);
    try {
      const res = await cloudApi.listProjects(trash, query || undefined);
      if (seq === searchSeq.current) {
        setProjects(res.projects);
      }
    } catch (err) {
      if (seq === searchSeq.current) {
        setError("genericError");
        setProjects([]);
      }
    }
  }, []);

  useEffect(() => {
    setProjects(null);
    const timer = setTimeout(() => reload(showTrash, search), search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [showTrash, search, reload]);

  useEffect(() => {
    cloudApi
      .accountUsage()
      .then(setUsage)
      .catch(() => {});
  }, [projects?.length]);

  const guarded = (fn: () => Promise<void>) => async () => {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError("genericError");
    } finally {
      setBusy(false);
    }
  };

  const openProject = (project: ProjectMeta) => {
    const doOpen = guarded(async () => {
      await cloudSync.openProject(project.id);
      onClose();
    });
    if (cloudSync.wouldClobberCanvas()) {
      setConfirm({
        title: t("overwriteWarningTitle"),
        message: t("overwriteWarningBody"),
        action: doOpen,
      });
    } else {
      doOpen();
    }
  };

  const startRename = (project: ProjectMeta) => {
    setEditingId(project.id);
    setEditingTitle(project.title);
  };

  const commitRename = guarded(async () => {
    if (!editingId || !editingTitle.trim()) {
      setEditingId(null);
      return;
    }
    const res = await cloudApi.renameProject(editingId, editingTitle.trim());
    cloudSync.onProjectRenamed(editingId, res.project.title);
    setEditingId(null);
    await reload(showTrash, search);
  });

  const duplicateProject = (project: ProjectMeta) =>
    guarded(async () => {
      await cloudApi.duplicateProject(project.id);
      await reload(showTrash, search);
    })();

  const deleteProject = (project: ProjectMeta) =>
    setConfirm({
      title: t("delete"),
      message: t("deleteConfirm"),
      action: guarded(async () => {
        await cloudApi.deleteProject(project.id);
        cloudSync.onProjectDeleted(project.id);
        await reload(showTrash, search);
      }),
    });

  const purgeProject = (project: ProjectMeta) =>
    setConfirm({
      title: t("deleteForever"),
      message: t("purgeConfirm"),
      action: guarded(async () => {
        await cloudApi.purgeProject(project.id);
        await reload(true);
      }),
    });

  return (
    <Dialog title={t("myProjects")} size="wide" onCloseRequest={onClose}>
      <div className="khiyarsa-cloud-projects">
        <div className="khiyarsa-cloud-projects__toolbar">
          <button
            className="khiyarsa-cloud-btn khiyarsa-cloud-btn--primary"
            disabled={busy}
            onClick={() => setDialog({ type: "saveAs" })}
          >
            {t("saveCanvasAsProject")}
          </button>
          <button
            className="khiyarsa-cloud-btn"
            disabled={busy}
            onClick={guarded(async () => {
              const project = await cloudSync.createBlankProject(t("untitled"));
              cloudSync.onProjectRenamed(project.id, project.title);
              onClose();
            })}
          >
            {t("newBlankProject")}
          </button>
          <span className="khiyarsa-cloud-projects__spacer" />
          <button
            className={`khiyarsa-cloud-btn khiyarsa-cloud-btn--link ${
              showTrash ? "active" : ""
            }`}
            onClick={() => setShowTrash(!showTrash)}
          >
            {showTrash ? t("backToProjects") : t("trash")}
          </button>
        </div>

        {!showTrash && (
          <TextField
            value={search}
            placeholder={t("searchProjects")}
            fullWidth
            type="search"
            onChange={setSearch}
          />
        )}

        {error && <div className="khiyarsa-cloud-error">{t("genericError")}</div>}

        {projects == null ? (
          <div className="khiyarsa-cloud-empty">…</div>
        ) : projects.length === 0 ? (
          <div className="khiyarsa-cloud-empty">
            {showTrash
              ? t("emptyTrash")
              : search
              ? t("noSearchResults")
              : t("noProjects")}
          </div>
        ) : (
          <ul className="khiyarsa-cloud-list">
            {projects.map((project) => (
              <li
                key={project.id}
                className={
                  attach?.projectId === project.id ? "is-attached" : ""
                }
              >
                <div
                  className="khiyarsa-cloud-list__thumb"
                  onClick={() => !showTrash && openProject(project)}
                >
                  {project.hasThumbnail ? (
                    <img
                      src={`${cloudApi.thumbnailUrl(project.id)}?r=${
                        project.contentRevision
                      }`}
                      alt=""
                      loading="lazy"
                    />
                  ) : (
                    <span className="khiyarsa-cloud-list__thumb-empty">
                      {LoadIcon}
                    </span>
                  )}
                </div>
                <div className="khiyarsa-cloud-list__meta">
                  {editingId === project.id ? (
                    <TextField
                      value={editingTitle}
                      fullWidth
                      selectOnRender
                      onChange={setEditingTitle}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          commitRename();
                        }
                        if (event.key === "Escape") {
                          setEditingId(null);
                        }
                      }}
                    />
                  ) : (
                    <div
                      className="khiyarsa-cloud-list__title"
                      onClick={() => !showTrash && openProject(project)}
                    >
                      {project.title}
                    </div>
                  )}
                  <div className="khiyarsa-cloud-list__sub">
                    {relativeTime(project.updatedAt, langCode)}
                  </div>
                </div>
                <div className="khiyarsa-cloud-list__actions">
                  {showTrash ? (
                    <>
                      <button
                        className="khiyarsa-cloud-btn"
                        disabled={busy}
                        onClick={guarded(async () => {
                          await cloudApi.restoreProject(project.id);
                          await reload(true);
                        })}
                      >
                        {t("restore")}
                      </button>
                      <button
                        className="khiyarsa-cloud-btn khiyarsa-cloud-btn--danger"
                        disabled={busy}
                        onClick={() => purgeProject(project)}
                      >
                        {t("deleteForever")}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="khiyarsa-cloud-btn"
                        disabled={busy}
                        onClick={() => openProject(project)}
                      >
                        {t("open")}
                      </button>
                      <button
                        className="khiyarsa-cloud-btn"
                        disabled={busy}
                        onClick={() => startRename(project)}
                      >
                        {t("rename")}
                      </button>
                      <button
                        className="khiyarsa-cloud-btn"
                        disabled={busy}
                        onClick={() => duplicateProject(project)}
                      >
                        {t("duplicate")}
                      </button>
                      <button
                        className="khiyarsa-cloud-btn"
                        title={t("history")}
                        disabled={busy}
                        onClick={() =>
                          setDialog({
                            type: "revisions",
                            projectId: project.id,
                            projectTitle: project.title,
                          })
                        }
                      >
                        {historyIcon}
                      </button>
                      <button
                        className="khiyarsa-cloud-btn khiyarsa-cloud-btn--danger"
                        title={t("delete")}
                        disabled={busy}
                        onClick={() => deleteProject(project)}
                      >
                        {TrashIcon}
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {user && (
          <div className="khiyarsa-cloud-projects__footer">
            <span>
              {t("signedInAs")} {user.displayName || user.email}
            </span>
            {usage && (
              <span>
                {usage.projects}/{usage.projectsLimit} ·{" "}
                {((usage.fileBytes + usage.sceneBytes) / (1024 * 1024)).toFixed(1)}{" "}
                MB {t("storageUsed")}
              </span>
            )}
          </div>
        )}
      </div>

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          onConfirm={() => {
            const action = confirm.action;
            setConfirm(null);
            action();
          }}
          onCancel={() => setConfirm(null)}
        >
          <p>{confirm.message}</p>
        </ConfirmDialog>
      )}
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Save-as dialog
// ---------------------------------------------------------------------------

const SaveAsDialog = ({ onClose }: { onClose: () => void }) => {
  const t = useCloudT();
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await cloudSync.createProjectFromCanvas(title.trim() || t("untitled"));
      onClose();
    } catch (err) {
      setError(t(errorKey(err)));
      setBusy(false);
    }
  };

  return (
    <Dialog title={t("saveCanvasAsProject")} size="small" onCloseRequest={onClose}>
      <div className="khiyarsa-cloud-auth">
        <TextField
          label={t("projectName")}
          value={title}
          fullWidth
          selectOnRender
          onChange={setTitle}
          onKeyDown={(event) => event.key === "Enter" && submit()}
        />
        {error && <div className="khiyarsa-cloud-error">{error}</div>}
        <button
          className="khiyarsa-cloud-btn khiyarsa-cloud-btn--primary"
          disabled={busy}
          onClick={submit}
        >
          {t("save")}
        </button>
      </div>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Revisions dialog
// ---------------------------------------------------------------------------

const RevisionsDialog = ({
  projectId,
  projectTitle,
  onClose,
}: {
  projectId: string;
  projectTitle: string;
  onClose: () => void;
}) => {
  const t = useCloudT();
  const [langCode] = useAppLangCode();
  const setDialog = useSetAtom(cloudDialogAtom);
  const [revisions, setRevisions] = useState<
    | { id: number; contentRevision: number; sceneSize: number; label: string | null; createdAt: string }[]
    | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState<number | null>(null);

  useEffect(() => {
    cloudApi
      .listRevisions(projectId)
      .then((res) => setRevisions(res.revisions))
      .catch(() => setRevisions([]));
  }, [projectId]);

  return (
    <Dialog
      title={`${t("revisions")} — ${projectTitle}`}
      size="regular"
      onCloseRequest={onClose}
    >
      <div className="khiyarsa-cloud-projects">
        <button
          className="khiyarsa-cloud-btn khiyarsa-cloud-btn--link"
          onClick={() => setDialog({ type: "projects" })}
        >
          ← {t("backToProjects")}
        </button>
        {revisions == null ? (
          <div className="khiyarsa-cloud-empty">…</div>
        ) : revisions.length === 0 ? (
          <div className="khiyarsa-cloud-empty">{t("noRevisions")}</div>
        ) : (
          <ul className="khiyarsa-cloud-list khiyarsa-cloud-list--plain">
            {revisions.map((rev) => (
              <li key={rev.id}>
                <div className="khiyarsa-cloud-list__meta">
                  <div className="khiyarsa-cloud-list__title">
                    {new Date(rev.createdAt).toLocaleString(
                      langCode?.startsWith("ar") ? "ar" : "en",
                    )}
                  </div>
                  <div className="khiyarsa-cloud-list__sub">
                    {(rev.sceneSize / 1024).toFixed(1)} KB
                    {rev.label ? ` — ${rev.label}` : ""}
                  </div>
                </div>
                <div className="khiyarsa-cloud-list__actions">
                  <button
                    className="khiyarsa-cloud-btn"
                    disabled={busy}
                    onClick={() => setConfirmId(rev.id)}
                  >
                    {t("restore")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {confirmId != null && (
        <ConfirmDialog
          title={t("restore")}
          onConfirm={async () => {
            setConfirmId(null);
            setBusy(true);
            try {
              await cloudSync.restoreRevision(projectId, confirmId);
              onClose();
            } finally {
              setBusy(false);
            }
          }}
          onCancel={() => setConfirmId(null)}
        >
          <p>{t("restoreRevisionConfirm")}</p>
        </ConfirmDialog>
      )}
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Conflict dialog
// ---------------------------------------------------------------------------

const ConflictDialog = () => {
  const t = useCloudT();
  const conflict = useAtomValue(cloudConflictAtom);
  const setDismissed = useSetAtom(conflictDialogDismissedAtom);
  const [busy, setBusy] = useState(false);

  if (!conflict) {
    return null;
  }

  const resolve = (fn: () => Promise<void>) => async () => {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      await fn();
      setDismissed(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={t("conflictTitle")}
      size="small"
      onCloseRequest={() => {
        // dismissing keeps the conflict active (sync stays paused); the
        // persistent banner re-opens this dialog
        setDismissed(true);
      }}
    >
      <div className="khiyarsa-cloud-auth">
        <p>{t("conflictBody")}</p>
        <button
          className="khiyarsa-cloud-btn khiyarsa-cloud-btn--primary"
          disabled={busy}
          onClick={resolve(() => cloudSync.resolveConflictKeepMine())}
        >
          {t("conflictKeepMine")}
        </button>
        <button
          className="khiyarsa-cloud-btn"
          disabled={busy}
          onClick={resolve(() => cloudSync.resolveConflictUseCloud())}
        >
          {t("conflictUseCloud")}
        </button>
        <button
          className="khiyarsa-cloud-btn"
          disabled={busy}
          onClick={resolve(() => cloudSync.resolveConflictSaveCopy())}
        >
          {t("conflictSaveCopy")}
        </button>
      </div>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Persistent sync banner — visible on all viewports (the status chip is
// desktop-only), so conflict/offline states are never invisible.
// ---------------------------------------------------------------------------

const CloudSyncBanner = () => {
  const t = useCloudT();
  const status = useAtomValue(syncStatusAtom);
  const attach = useAtomValue(attachedProjectAtom);
  const conflict = useAtomValue(cloudConflictAtom);
  const [dismissed, setDismissed] = useAtom(conflictDialogDismissedAtom);

  if (conflict && dismissed) {
    return (
      <div
        className="khiyarsa-cloud-banner khiyarsa-cloud-banner--danger"
        onClick={() => setDismissed(false)}
        role="button"
      >
        {t("bannerConflict")}
      </div>
    );
  }
  if (status === "offline" && attach) {
    return (
      <div className="khiyarsa-cloud-banner khiyarsa-cloud-banner--warning">
        {t("bannerOffline")}
      </div>
    );
  }
  return null;
};

// ---------------------------------------------------------------------------
// Status chip (rendered in renderTopRightUI)
// ---------------------------------------------------------------------------

export const CloudStatusChip = () => {
  const t = useCloudT();
  const attach = useAtomValue(attachedProjectAtom);
  const status = useAtomValue(syncStatusAtom);
  const user = useAtomValue(cloudUserAtom);
  const setDialog = useSetAtom(cloudDialogAtom);

  if (!user || !attach) {
    return null;
  }

  const statusLabel =
    status === "saving"
      ? t("statusSaving")
      : status === "offline"
      ? t("statusOffline")
      : status === "conflict"
      ? t("statusConflict")
      : t("statusSaved");

  return (
    <div
      className={`khiyarsa-cloud-chip khiyarsa-cloud-chip--${status}`}
      onClick={() => setDialog({ type: "projects" })}
      title={statusLabel}
    >
      <span className="khiyarsa-cloud-chip__dot" />
      <span className="khiyarsa-cloud-chip__title">{attach.title}</span>
      <button
        className="khiyarsa-cloud-chip__close"
        title={t("closeProject")}
        onClick={(event) => {
          event.stopPropagation();
          cloudSync.detach();
        }}
      >
        ×
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

export const KhiyarsaCloudUI = () => {
  const [dialog, setDialog] = useAtom(cloudDialogAtom);
  const conflict = useAtomValue(cloudConflictAtom);
  const dismissed = useAtomValue(conflictDialogDismissedAtom);
  const close = useCallback(() => setDialog({ type: "none" }), [setDialog]);

  return (
    <>
      <CloudSyncBanner />
      {dialog.type === "auth" && <AuthDialog onClose={close} />}
      {dialog.type === "projects" && <ProjectsDialog onClose={close} />}
      {dialog.type === "saveAs" && <SaveAsDialog onClose={close} />}
      {dialog.type === "revisions" && (
        <RevisionsDialog
          projectId={dialog.projectId}
          projectTitle={dialog.projectTitle}
          onClose={close}
        />
      )}
      {conflict && !dismissed && dialog.type === "none" && <ConflictDialog />}
    </>
  );
};
